import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBookmark, FaHeart, FaComment, FaShare, FaTrash } from 'react-icons/fa';
import { bookmarksAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Link } from 'react-router-dom';
import VerificationBadge from '../components/VerificationBadge';

const Bookmarks = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    try {
      const response = await bookmarksAPI.getAll();
      setBookmarks(response.data || []);
    } catch (error) {
      console.error('Failed to load bookmarks', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBookmark = async (postId) => {
    try {
      await bookmarksAPI.toggle(postId);
      setBookmarks(bookmarks.filter(b => b.id !== postId));
      toast.success('Removed from bookmarks');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        
        <main className="col-span-12 md:col-span-10 p-6 md:p-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] rounded-full flex items-center justify-center">
                <FaBookmark className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-outfit font-bold text-gray-900">Bookmarks</h1>
                <p className="text-gray-500">{bookmarks.length} saved points</p>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white rounded-xl h-40 animate-pulse" />
                ))}
              </div>
            ) : bookmarks.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <FaBookmark className="text-5xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No bookmarks yet</h3>
                <p className="text-gray-500">Save points you want to revisit later</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookmarks.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <Link to={`/profile/${post.user_id}`}>
                          <img
                            src={post.avatar_url || `https://ui-avatars.com/api/?name=${post.full_name}`}
                            alt={post.full_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        </Link>
                        <div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/profile/${post.user_id}`} className="font-bold text-gray-900 hover:text-[#4A90E2]">
                              {post.full_name}
                            </Link>
                            {post.is_verified && (
                              <VerificationBadge badgeType={post.verification_badge} size="sm" />
                            )}
                          </div>
                          <p className="text-gray-500 text-sm">@{post.username} · {formatTimeAgo(post.created_at)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveBookmark(post.id)}
                        className="text-gray-400 hover:text-red-500 p-2 opacity-0 group-hover:opacity-100 transition-all"
                        title="Remove bookmark"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <p className="text-gray-700 mb-4">{post.content}</p>

                    {post.media_url && (
                      <img
                        src={post.media_url}
                        alt=""
                        className="w-full rounded-xl max-h-96 object-cover mb-4"
                      />
                    )}

                    <div className="flex items-center space-x-6 pt-4 border-t border-gray-100 text-gray-500">
                      <span className="flex items-center space-x-1">
                        <FaHeart className="text-red-400" />
                        <span>{post.likes_count}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <FaComment className="text-[#4A90E2]" />
                        <span>{post.comments_count}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <FaShare className="text-green-500" />
                        <span>{post.shares_count}</span>
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Bookmarks;
